import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, categoryAPI } from '../../config/api';
import { categoryPayload, getUploadUrl } from '../../utils/adminFields';
import resolveImageUrl from '../../utils/resolveImageUrl';
import AdminSidebar from '../../components/AdminSidebar';
import { showAppToast } from '../../utils/notifications';

function AddCategory({ onSuccess }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    categoryName: '',
    description: '',
    imageUrl: '',
  });
  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: '' }));
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const imageForm = new FormData();
    imageForm.append('file', file);
    setLoading(true);
    try {
      const response = await adminAPI.uploadImage(imageForm);
      const url = getUploadUrl(response);
      setFormData((current) => ({ ...current, imageUrl: url }));
      showAppToast({ message: 'Image uploaded successfully', type: 'success', duration: 2200 });
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Upload failed', type: 'error' });
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.categoryName.trim()) nextErrors.categoryName = 'Category name is required';
    if (!formData.description.trim()) nextErrors.description = 'Description is required';
    if (!formData.imageUrl.trim()) nextErrors.imageUrl = 'Category image URL is required';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      showAppToast({ message: 'Please fill in all required fields', type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      await categoryAPI.create(categoryPayload(formData));
      showAppToast({ message: 'Category added successfully', type: 'success', duration: 2400 });
      if (onSuccess) onSuccess();
      navigate('/admin/category');
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to create category', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content px-4 py-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 text-center">
          <p className="text-accent">Admin</p>
          <h1 className="section-title text-4xl text-primary-theme">Add Category</h1>
        </div>

        <form onSubmit={handleSubmit} className="admin-form-card card-luxury">
          <label className="admin-field">
            <span>Name</span>
            <input
              type="text"
              name="categoryName"
              value={formData.categoryName}
              onChange={handleChange}
              required
            />
            {errors.categoryName && <small>{errors.categoryName}</small>}
          </label>

          <label className="admin-field">
            <span>Description</span>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              required
            />
            {errors.description && <small>{errors.description}</small>}
          </label>

          <label className="admin-field">
            <span>Image URL</span>
            <input
              type="text"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleChange}
              required
            />
            {errors.imageUrl && <small>{errors.imageUrl}</small>}
          </label>

          <div className="admin-upload-row">
            <input type="file" accept="image/*" onChange={handleUpload} />
            {formData.imageUrl && (
              <img src={resolveImageUrl(formData.imageUrl)} alt="Category preview" />
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button type="submit" disabled={loading} className="btn-luxury">
              {loading ? 'Saving...' : 'Add Category'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/category')}
              disabled={loading}
              className="btn-luxury-outline"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
    </div>

  );
}

export default AddCategory;
